import {Component, DestroyRef, ElementRef, afterRenderEffect, computed, effect, inject, signal, viewChildren} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { ReviewDto } from '../../models/restaurant-api.model';
import { ReviewsService } from '../../services/reviews.service';
import { RestaurantConfigService } from '../../services/restaurant-config.service';

interface DisplayRow {
  trackKey: string;
  name: string;
  text: string;
  rating: number;
}

function toDisplayRows(reviews: ReviewDto[]): DisplayRow[] {
  return reviews.map((r, i) => ({
    trackKey: `review-${i}-${r.authorName}-${r.text.slice(0, 24)}`,
    name: r.authorName?.trim() || 'Vendég',
    text: r.text,
    rating: Math.max(0, Math.min(5, Math.round(Number(r.rating)))),
  }));
}

@Component({
  selector: 'app-testimonials',
  templateUrl: './testimonials.component.html',
  styleUrl: './testimonials.component.scss',
})
export class TestimonialsComponent {
  private readonly restaurantConfig = inject(RestaurantConfigService);
  private readonly reviewsService = inject(ReviewsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly reviews = signal<ReviewDto[]>([]);
  private readonly textElements = viewChildren<ElementRef<HTMLElement>>('reviewText');
  private readonly overflowingKeys = signal<ReadonlySet<string>>(new Set());
  private readonly expandedKeys = signal<ReadonlySet<string>>(new Set());

  private fetchStarted = false;

  readonly rows = computed<DisplayRow[]>(() => toDisplayRows(this.reviews()));
  readonly visible = computed(
    () => this.restaurantConfig.config().features.googleReviews && this.rows().length > 0
  );

  constructor() {
    effect(() => {
      const enabled = this.restaurantConfig.config().features.googleReviews;
      const ready = this.restaurantConfig.initialized();
      if (!enabled || !ready || this.fetchStarted) {
        return;
      }
      this.fetchStarted = true;
      this.reviewsService
        .getReviews()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((reviews) => {
          if (reviews.length > 0) {
            this.reviews.set(reviews);
          }
        });
    });

    afterRenderEffect(() => {
      const refs = this.textElements();
      if (refs.length === 0) {
        return;
      }
      const current = this.overflowingKeys();
      const next = new Set(current);
      let changed = false;

      for (const ref of refs) {
        const el = ref.nativeElement;
        const key = el.dataset['key'];
        if (!key) {
          continue;
        }
        if (current.has(key)) {
          continue;
        }
        if (el.classList.contains('is-expanded')) {
          continue;
        }
        if (el.scrollHeight > el.clientHeight + 1) {
          next.add(key);
          changed = true;
        }
      }

      if (changed) {
        this.overflowingKeys.set(next);
      }
    });
  }

  isExpanded(key: string): boolean {
    return this.expandedKeys().has(key);
  }

  isOverflowing(key: string): boolean {
    return this.overflowingKeys().has(key);
  }

  toggleExpanded(key: string): void {
    this.expandedKeys.update((set) => {
      const next = new Set(set);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  getStars(rating: number): number[] {
    return Array.from({ length: rating }, (_, i) => i);
  }

  avatarLetter(name: string): string {
    const c = name.trim().charAt(0);
    return c ? c.toUpperCase() : '?';
  }
}
